import { Suspense, lazy, useState, useSyncExternalStore } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from './lib/api'
import { hasSession, onSessionLost, adoptFromUrlHash } from './lib/session'
import { hasWelcomed, landingFor, savedIntent, type Intent } from './lib/guest'
import { IS_QAFILA, IS_USTOOL } from './lib/appKind'
import { IS_DIRECTORY } from './lib/market'
import { I18nProvider } from './lib/i18n'
import { Spinner } from './components/ui'
import { AskProvider } from './components/ask'
import { AskPanel } from './components/AskPanel'
import { LiquidTabBar, type TabDef } from './components/LiquidTabBar'
import SignIn from './screens/SignIn'
import Welcome from './screens/Welcome'
import Onboarding from './screens/Onboarding'
import Activity from './screens/Activity'

/**
 * The mobile client app.
 *
 * FIVE TABS, NOT A MENU. Everything a client does is one of five verbs, and a
 * phone shows those as a bar the thumb can reach rather than a hamburger that
 * hides them. Admin is deliberately absent: it belongs on a desktop, and
 * app.hundai.tech still has it.
 *
 * Detail screens are routes, not tabs, so the browser Back button and the
 * platform back gesture both do the obvious thing. They animate in from the
 * right, which is the only cue that says "you went deeper" rather than
 * "the page changed".
 */

// Split the heavier screens out of the first paint. A phone on mobile data
// should not download the seat map to look at its shipments.
const Charter = lazy(() => import('./screens/Charter'))
const TDashboard = lazy(() => import('./screens/TDashboard'))
const TRequests = lazy(() => import('./screens/TRequests'))
const TBusiness = lazy(() => import('./screens/TBusiness'))
const AdminCarriers = lazy(() => import('./screens/AdminCarriers'))
const WrongApp = lazy(() => import('./screens/WrongApp'))
const CarrierWelcome = lazy(() => import('./screens/CarrierWelcome'))
const TEarnings = lazy(() => import('./screens/TEarnings'))
const TSite = lazy(() => import('./screens/TSite'))
const TLive = lazy(() => import('./screens/TLive'))
/*
 * THE DIRECTORY (§81.3). Qafila's three screens, and the only passenger
 * screens in a Saudi build.
 */
const Directory = lazy(() => import('./screens/Directory'))
const CarrierPage = lazy(() => import('./screens/CarrierPage'))
const MyTravel = lazy(() => import('./screens/MyTravel'))
const Pay = lazy(() => import('./screens/Pay'))
const Send = lazy(() => import('./screens/Send'))
const Travel = lazy(() => import('./screens/Travel'))
const Tickets = lazy(() => import('./screens/Tickets'))
const Account = lazy(() => import('./screens/Account'))
const OrderDetail = lazy(() => import('./screens/OrderDetail'))
const RequestDetail = lazy(() => import('./screens/RequestDetail'))
const TicketDetail = lazy(() => import('./screens/TicketDetail'))
const Terms = lazy(() => import('./screens/Terms'))

// A magic link or a Google redirect hands the session back in the URL
// fragment, and this app defined adoptFromUrlHash() without ever calling it:
// the tokens arrived, sat in the address bar, and were thrown away on the
// first navigation while the app showed the sign-in form. Adopt before the
// first render, the same as app.hundai.tech has always done.
adoptFromUrlHash()

interface Me {
  profile: {
    id: string; role: string; full_name: string | null; email: string
    onboarded_at: string | null; locale: string | null
  }
}

/*
 * THREE TABS IN THE DIRECTORY BUILD, FIVE IN THE MARKETPLACE.
 *
 * §81.3 drew four -- Carriers, Guides, My travel, Account. Guides is not
 * built, and a tab that opens an empty screen is worse than a tab that is
 * not there; it goes in when there is something written behind it.
 *
 * What is gone is gone for a reason, not for tidiness. Book now, Journeys
 * and the dashboard all assumed WE hold the request, the departure and the
 * ticket. In the Kingdom we may not: arranging transport between a traveller
 * and a carrier is a licensed activity, so the request is composed on the
 * carrier's own site, the ticket is his, and there is nothing of it here to
 * put behind a tab.
 */
const DIR_TABS: TabDef[] = [
  // The van glyph, not a house: this tab IS the product, and what it opens
  // is a list of companies.
  { to: '/', key: 'nav.companies', icon: 'send', home: true },
  { to: '/mine', key: 'nav.myTravel', icon: 'ticket' },
  { to: '/account', key: 'nav.account', icon: 'person', unread: true },
]

const MARKET_TABS: TabDef[] = [
  // One door to arranging transport. Which kind is a switch at the top of the
  // page, not a decision the tab bar forces before the page is open.
  // `home: true` -- tapping it from three steps into the booking flow has
  //  to return to "where are you starting?", not just light the tab.
  { to: '/travel', key: 'nav.bookNow', icon: 'seat', also: /^\/(send|charter)$/, home: true },
  { to: '/tickets', key: 'nav.journeys', icon: 'ticket' },
  { to: '/dashboard', key: 'nav.dashboard', icon: 'home', also: /^\/$/ },
  { to: '/account', key: 'nav.account', icon: 'person', unread: true },
]

const TABS: TabDef[] = IS_DIRECTORY ? DIR_TABS : MARKET_TABS

/*
 * The transporter's four.
 *
 * §53.4 settled this: ONE app, the shell switched on profile.role. Most
 * Makkah operators are one- or two-vehicle outfits who are also travellers,
 * so two store listings would double the release burden for no user
 * benefit -- and splitting later is cheap where merging later is not.
 *
 * Until today a transporter signing in here got the passenger shell and a
 * 403 on the first button they pressed.
 */
/*
 * The admin's two.
 *
 * Deliberately not a copy of the transporter's bar with one swapped: an admin
 * has no fleet, no requests and no earnings, and showing four tabs where two
 * lead somewhere is how a back office starts growing in a phone app. The
 * carrier queue is the job; Account is how they get out.
 */
const A_TABS: TabDef[] = [
  { to: '/', key: 'nav.carriers', icon: 'check', home: true },
  { to: '/account', key: 'nav.account', icon: 'person', unread: true },
]

/*
 * FIVE NOW, NOT FOUR.
 *
 * His own website is where a carrier spends the minutes before a season
 * opens -- his prices, his languages, his hours -- and burying it inside
 * Business would make the one screen that is visible to his customers the
 * hardest one to find. The globe is the icon because what it opens is a
 * public address.
 */
const T_TABS: TabDef[] = [
  { to: '/', key: 'nav.dashboard', icon: 'home', home: true },
  { to: '/requests', key: 'nav.requests', icon: 'ticket' },
  { to: '/website', key: 'tsite.eyebrow', icon: 'globe' },
  { to: '/business', key: 'nav.business', icon: 'send' },
  { to: '/account', key: 'nav.account', icon: 'person', unread: true },
]

/**
 * Routes that own the whole screen.
 *
 * Detail screens, plus /send -- which is a flow with its own back action and a
 * pinned submit button, the same way a composer takes over in any app with a
 * create tab. /travel is NOT here: it is a tab root, and a tab that hides the
 * tab bar leaves no way back except the platform gesture.
 */
const FULL_SCREEN = [/^\/orders\//, /^\/requests\//, /^\/tickets\/[^/]+$/, /^\/send$/,
                     // One carrier's page is a push, not a tab root.
                     /^\/carriers\//]

/**
 * The provider has to be OUTSIDE every early return.
 *
 * It was inside, so the loading spinner and the onboarding screen -- both of
 * which call useT -- rendered before any provider existed and threw
 * "useI18n must be used inside I18nProvider" straight to a blank page. The
 * profile locale is not known until /me resolves, which is exactly why it is
 * tempting to wait for it first; the answer is to render the provider with
 * `undefined` and let it adopt the locale when it arrives.
 */
function Shell({ signedIn }: { signedIn: boolean }) {
  // /me requires a caller. A guest has none, so the query is disabled rather
  // than fired and caught -- an unauthenticated 401 on every cold start is
  // how the §37 request storm looked from the outside, and a guest session
  // would reproduce it on every page load.
  const me = useQuery<Me>({
    queryKey: ['me'], queryFn: () => api('/me'), retry: false, enabled: signedIn,
  })
  return (
    <I18nProvider profileLocale={me.data?.profile.locale}>
      <Inner me={me.data} signedIn={signedIn}
             loading={signedIn && me.isLoading} failed={signedIn && me.isError}
             onOnboarded={() => void me.refetch()} />
    </I18nProvider>
  )
}

function Inner({ me, signedIn, loading, failed, onOnboarded }: {
  me: Me | undefined
  signedIn: boolean
  loading: boolean
  failed: boolean
  onOnboarded: () => void
}) {
  const { pathname } = useLocation()
  const role = me?.profile.role
  const isTransporter = role === 'transporter'
  // A profile holds one role, so the owner's carrier account is not also the
  // admin that approves carriers -- which is the right way round. Signing in
  // as admin@hundai.tech is what opens the queue.
  const isAdmin = role === 'admin'

  /*
   * THE WRONG APP FOR THIS ACCOUNT.
   *
   * "i want ustool a seperate app altogether no one should see others app".
   * So the app you installed decides what exists, and an account that does
   * not belong in it is shown a door rather than the other app's tab bar:
   *
   *   Ustool  carriers and the admin who approves them. A passenger account
   *           is refused -- a pilgrim has nothing to do in a carrier app.
   *   Qafila  passengers. A transporter account is refused, which is the
   *           behaviour that changed: signing into Qafila as a transporter
   *           used to hand over the whole transporter shell.
   *
   * A DRIVER is refused by both, as before -- Qaid is their app, and it is
   * native because a WebView cannot report GPS with the screen off.
   *
   * Guests are never refused: browsing is open in both, and the sign-in is
   * where the mismatch becomes knowable.
   */
  const wrongApp = Boolean(role) && (
    role === 'driver'
    || (IS_USTOOL && role === 'client')
    || (IS_QAFILA && (role === 'transporter' || role === 'admin'))
  )

  /*
   * Signed out in Ustool. No tab bar and no passenger routes exist here --
   * there is nothing for a carrier to browse, because GET /requests needs an
   * account, so the honest first screen is sign in or apply.
   */
  if (IS_USTOOL && !signedIn) return <CarrierWelcome />

  if (loading) return <Spinner />
  // app.hundai.tech has always done this. The phone fell through to the full
  // shell with me === undefined, so a session that had died rendered
  // "Missing bearer token" INSIDE the tab bar and sat there -- which is the
  // state every one of the observed request storms was running in.
  if (failed) return <SignIn />
  if (signedIn && me && !me.profile.onboarded_at) return <Onboarding onDone={onOnboarded} />

  return (
    <AskProvider>
      <div className="h-full">
        {/* Keyed on pathname so a push actually re-runs the animation
            instead of React reusing the node and showing nothing. */}
        <div key={pathname} className={`h-full ${
          FULL_SCREEN.some((r) => r.test(pathname)) ? 'animate-pushIn' : ''}`}>
          <Suspense fallback={<Spinner />}>
            {wrongApp ? (
              <WrongApp role={role!} />
            ) : isAdmin ? (
              <Routes>
                <Route path="/" element={<AdminCarriers />} />
                <Route path="/account" element={<Account />} />
                <Route path="/terms" element={<Terms />} />
                {/* Same reasoning as the transporter's catch-all: every
                    passenger screen would 403 one tap later. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : isTransporter ? (
              <Routes>
                <Route path="/" element={<TDashboard />} />
                <Route path="/requests" element={<TRequests />} />
                <Route path="/requests/:id" element={<RequestDetail />} />
                <Route path="/website" element={<TSite />} />
                <Route path="/business" element={<TBusiness />} />
                <Route path="/earnings" element={<TEarnings />} />
                <Route path="/trips/:id/live" element={<TLive />} />
                <Route path="/account" element={<Account />} />
                <Route path="/terms" element={<Terms />} />
                {/* A transporter has no business on the passenger screens,
                    and sending them to Book now would be the same 403 one
                    tap later. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : (
            IS_DIRECTORY ? (
              /*
               * THE DIRECTORY. Three screens and no flow.
               *
               * Nothing here needs an account, and nothing here is guarded:
               * looking up who is licensed must not require signing in, and
               * the travel card is on the device. The sign-in that remains
               * is for Account alone.
               */
              <Routes>
                <Route path="/" element={<Directory />} />
                <Route path="/carriers/:id" element={<CarrierPage />} />
                <Route path="/mine" element={<MyTravel />} />
                <Route path="/account" element={<Account />} />
                <Route path="/terms" element={<Terms />} />
                {/*
                  Every old passenger address lands on the carrier list.
                  /travel, /charter, /send, /tickets and /orders/:id were
                  all bookmarkable and some are in sent emails; a 404 would
                  be the app looking broken to the people who used it most.
                */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : (
            <Routes>
              {/* Browsing, pricing and building a request all work signed
                  out. These do not: every one of them asks the API for
                  something that belongs to a particular person, and a guest
                  reaching one used to get a raw "Missing bearer token" in a
                  red box -- the §37 failure mode, reintroduced the moment
                  guests were let into the shell. */}
              <Route path="/" element={
                signedIn ? <Activity /> : <Navigate to="/travel" replace />} />
              <Route path="/dashboard" element={
                <MineOnly in={signedIn}><Activity /></MineOnly>} />
              <Route path="/charter" element={<Charter />} />
              <Route path="/send" element={<Send />} />
              <Route path="/travel" element={<Travel />} />
              <Route path="/tickets" element={<MineOnly in={signedIn}><Tickets /></MineOnly>} />
              <Route path="/tickets/:id" element={<MineOnly in={signedIn}><TicketDetail /></MineOnly>} />
              <Route path="/orders/:id" element={<MineOnly in={signedIn}><OrderDetail /></MineOnly>} />
              <Route path="/orders/:id/pay" element={<MineOnly in={signedIn}><Pay /></MineOnly>} />
              <Route path="/requests/:id" element={<MineOnly in={signedIn}><RequestDetail /></MineOnly>} />
              <Route path="/account" element={<Account />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<Navigate to="/travel" replace />} />
            </Routes>
            )
            )}
          </Suspense>
        </div>
        {/* Both stay on every screen. A detail page that hid the bar left
            the platform gesture as the only way out, and put the assistant
            out of reach exactly where someone is most likely to have a
            question about what they are looking at. */}
        <AskPanel />
        {/* No bar on the refusal screen: it is a dead end on purpose, and a
            tab bar is an invitation to go further in. */}
        {!wrongApp && (
          <LiquidTabBar tabs={isAdmin ? A_TABS : isTransporter ? T_TABS : TABS} />
        )}
      </div>
    </AskProvider>
  )
}


/**
 * hasSession() is a storage read, not state, so React never learned that the
 * session it rendered with had been thrown away -- the app stayed in its
 * signed-in shell around queries that could only 401. Subscribing to the
 * loss event is what turns that into a re-render and a sign-in form.
 */
function useSignedIn(): boolean {
  return useSyncExternalStore(onSessionLost, hasSession)
}

/**
 * No sign-in at the door.
 *
 * The owner: "remove the login screen from beginning it will only be
 * required before sending the request take it inside". So the order is
 * welcome -> browse -> and the code is asked for at the one moment it buys
 * something, which the send and book flows own themselves.
 *
 * useSignedIn still subscribes to session loss, because a session that dies
 * mid-visit must drop the person back to browsing rather than leave a shell
 * full of queries that can only 401.
 */
export default function App() {
  const signedIn = useSignedIn()
  /*
   * /welcome always shows it.
   *
   * The screen is answered once per browser, which is right for a visitor
   * and useless for anyone checking the app: once you have clicked through
   * it, the only way back was Account, and you have to know that. A plain
   * address that always opens it costs nothing and is the thing to hand
   * someone who asks "why am I not seeing it".
   */
  const forced = window.location.pathname === '/welcome'
  /*
   * Answered once per BROWSER, and having a session is not an answer.
   *
   * This read `signedIn || hasWelcomed()`, on the reasoning that anyone with
   * a session had been here before. The effect was that the screen vanished
   * for good the moment someone signed in -- language and intent, the two
   * questions the app opens with, became unreachable on their own device.
   * The flag alone is the right test; Account reopens it (reopenWelcome).
   */
  /*
   * Ustool never asks the pilgrim welcome.
   *
   * Its three intents are charter / book a seat / send something -- all
   * passenger questions, and showing them to a carrier is the whole thing
   * the owner asked to stop. Ustool's own first screen is CarrierWelcome,
   * rendered by the shell below.
   */
  const [welcomed, setWelcomed] = useState(() => IS_USTOOL || (!forced && hasWelcomed()))
  // Above the early return: a hook called after one runs on some renders and
  // not others, which is the crash §(conditional hooks) already cost a day.
  const navigate = useNavigate()

  if (!welcomed) {
    return (
      <I18nProvider>
        <Welcome onDone={(intent: Intent) => {
          /*
           * navigate(), NOT history.replaceState().
           *
           * replaceState changes the address bar and tells React Router
           * nothing -- it fires no navigation event, so the router kept the
           * location it mounted with ('/') and rendered the dashboard no
           * matter which intent was chosen. Someone who picked "charter a
           * full vehicle" and pressed Start landed on the dashboard, which
           * is what the owner reported.
           *
           * Still replace, not push: Back from the first screen should leave
           * the app, not reopen a question that has been answered.
           */
          navigate(landingFor(intent), { replace: true })
          setWelcomed(true)
        }} />
      </I18nProvider>
    )
  }
  return <Shell signedIn={signedIn} />
}

/** What the person said they came for, for the screens that branch on it. */
export { savedIntent }

/**
 * A screen that only means anything when there is an account behind it.
 *
 * Shows the sign-in rather than a redirect, because the person arrived here
 * deliberately -- usually from a link to their own booking -- and bouncing
 * them to Book now loses what they came for. SignIn reloads the app on
 * success, which lands them back on this URL with a session.
 */
function MineOnly({ in: signedIn, children }: {
  in: boolean
  children: React.ReactNode
}) {
  if (!signedIn) return <SignIn />
  return <>{children}</>
}
